import type { Plane3, Segment3, Vec3 } from './viewTypes'

// ---------------------------------------------------------------------------
// Intersecção plano-triângulo (o "corte" propriamente dito).
//
// Cada triângulo dá 0 ou 1 segmento. Regras de degenerescência:
//  - triângulo com os três vértices no plano (coplanar): IGNORA-SE. Um
//    triângulo coplanar não é uma linha de corte, é uma face rasante; se
//    o emitíssemos, ficávamos com o contorno duplicado da face inteira.
//  - triângulo que só TOCA o plano num vértice: 1 ponto, sem segmento.
//  - triângulo com uma aresta inteira no plano: emite essa aresta. Se
//    houver um triângulo vizinho na mesma condição, o segmento sai
//    duplicado; o assembleLoops despista duplicados.
//  - segmento de comprimento nulo: descartado.
//
// Tolerância: `eps` em METROS (distância ao plano abaixo da qual um
// vértice se considera EM cima do plano). 1e-6 (um mícron) é folgado
// para modelos de edifícios e evita fatias de triângulo absurdas por
// ruído de vírgula flutuante.
// ---------------------------------------------------------------------------

export const CUT_EPS = 1e-6

/** Corta uma malha triangulada por um plano. `positions` é xyz
 *  intercalado em coordenadas de mundo, `indices` pode ser null (a malha
 *  é lida como triplos consecutivos de vértices). */
export function cutTriangles(
  positions: Float32Array | number[],
  indices: Uint32Array | Uint16Array | number[] | null,
  plane: Plane3,
  eps: number = CUT_EPS,
): Segment3[] {
  const out: Segment3[] = []
  if (!positions || positions.length < 9) return out
  const nx = plane.normal[0]
  const ny = plane.normal[1]
  const nz = plane.normal[2]
  const ox = plane.origin[0]
  const oy = plane.origin[1]
  const oz = plane.origin[2]

  const triCount = indices
    ? Math.floor(indices.length / 3)
    : Math.floor(positions.length / 9)

  // Distância com sinal do vértice `vi` ao plano.
  const dist = (vi: number): number =>
    (positions[vi * 3] - ox) * nx +
    (positions[vi * 3 + 1] - oy) * ny +
    (positions[vi * 3 + 2] - oz) * nz

  const point = (vi: number): Vec3 => [
    positions[vi * 3],
    positions[vi * 3 + 1],
    positions[vi * 3 + 2],
  ]

  const hit: Vec3[] = []

  for (let t = 0; t < triCount; t++) {
    const i0 = indices ? indices[t * 3] : t * 3
    const i1 = indices ? indices[t * 3 + 1] : t * 3 + 1
    const i2 = indices ? indices[t * 3 + 2] : t * 3 + 2
    const d0 = dist(i0)
    const d1 = dist(i1)
    const d2 = dist(i2)
    if (!Number.isFinite(d0) || !Number.isFinite(d1) || !Number.isFinite(d2)) {
      continue
    }
    const s0 = d0 > eps ? 1 : d0 < -eps ? -1 : 0
    const s1 = d1 > eps ? 1 : d1 < -eps ? -1 : 0
    const s2 = d2 > eps ? 1 : d2 < -eps ? -1 : 0

    // Coplanar: face rasante, não é linha de corte.
    if (s0 === 0 && s1 === 0 && s2 === 0) continue
    // Todo de um lado (sem tocar): nada a cortar.
    if (s0 !== 0 && s0 === s1 && s1 === s2) continue

    hit.length = 0
    const idx = [i0, i1, i2]
    const sgn = [s0, s1, s2]
    const dst = [d0, d1, d2]
    for (let e = 0; e < 3; e++) {
      const a = e
      const b = (e + 1) % 3
      if (sgn[a] === 0) {
        pushUnique(hit, point(idx[a]), eps)
      }
      if (sgn[a] !== 0 && sgn[b] !== 0 && sgn[a] !== sgn[b]) {
        const da = dst[a]
        const db = dst[b]
        const tt = da / (da - db)
        const pa = point(idx[a])
        const pb = point(idx[b])
        pushUnique(
          hit,
          [
            pa[0] + (pb[0] - pa[0]) * tt,
            pa[1] + (pb[1] - pa[1]) * tt,
            pa[2] + (pb[2] - pa[2]) * tt,
          ],
          eps,
        )
      }
    }
    if (hit.length !== 2) continue
    const a = hit[0]
    const b = hit[1]
    if (dist3(a, b) <= eps) continue
    out.push({ a: [a[0], a[1], a[2]], b: [b[0], b[1], b[2]] })
  }

  return out
}

function dist3(a: Vec3, b: Vec3): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2])
}

/** Acrescenta o ponto se ainda não houver um coincidente (evita contar
 *  duas vezes um vértice que está em cima do plano e é partilhado por
 *  duas arestas do triângulo). */
function pushUnique(list: Vec3[], p: Vec3, eps: number): void {
  for (const q of list) {
    if (dist3(p, q) <= eps) return
  }
  list.push(p)
}
